<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ListAttribute from 'dashboard/components-next/CustomAttributes/ListAttribute.vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';
import DateAttribute from 'dashboard/components-next/CustomAttributes/DateAttribute.vue';
import OtherAttribute from 'dashboard/components-next/CustomAttributes/OtherAttribute.vue';
import TeamNumbersAPI from 'dashboard/api/aceleres/teamNumbers';
import TeamNumberRemoveModal from 'dashboard/routes/dashboard/settings/teamNumbers/TeamNumberRemoveModal.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
  selectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['teamNumberRemoved']);
const showRemoveModal = ref(false);
const removeBusy = ref(false);
const removeError = ref('');
const hasAcelerity = ref(false);

const store = useStore();
const { t } = useI18n();
const route = useRoute();

const handleDelete = async () => {
  try {
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: route.params.contactId,
      customAttributes: [props.attribute.attributeKey],
    });
    useAlert(
      t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.DELETE_SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.DELETE_ERROR')
    );
  }
};

const handleUpdate = async value => {
  if (
    props.attribute.attributeKey === 'numero_equipe' &&
    value === false &&
    props.attribute.value
  ) {
    removeError.value = '';
    showRemoveModal.value = true;
    try {
      const { data } = await TeamNumbersAPI.state(route.params.contactId);
      if (showRemoveModal.value)
        hasAcelerity.value = data.payload?.has_acelerity === true;
    } catch {
      hasAcelerity.value = false;
    }
    return;
  }
  try {
    await store.dispatch('contacts/update', {
      id: route.params.contactId,
      customAttributes: {
        [props.attribute.attributeKey]: value,
      },
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.message ||
        t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.UPDATE_ERROR')
    );
  }
};

const confirmRemove = async () => {
  if (removeBusy.value) return;
  removeBusy.value = true;
  removeError.value = '';
  const id = route.params.contactId;
  try {
    await TeamNumbersAPI.unmark(id);
    emit('teamNumberRemoved', {
      id,
      name: props.selectedContact?.name || 'Contato',
    });
    showRemoveModal.value = false;
    store.dispatch('contacts/show', { id }).catch(() => null);
  } catch {
    removeError.value = 'Não foi possível remover agora. Tente novamente.';
  } finally {
    removeBusy.value = false;
  }
};

const componentMap = {
  list: ListAttribute,
  checkbox: CheckboxAttribute,
  date: DateAttribute,
  default: OtherAttribute,
};

const CurrentAttributeComponent = computed(() => {
  return (
    componentMap[props.attribute.attributeDisplayType] || componentMap.default
  );
});
</script>

<template>
  <div
    class="grid grid-cols-[140px,1fr] group/attribute items-center w-full gap-2"
    :class="isEditingView ? 'min-h-10' : 'min-h-11'"
  >
    <div class="flex items-center justify-between truncate">
      <span class="text-sm font-medium truncate text-n-slate-12">
        {{ attribute.attributeDisplayName }}
      </span>
    </div>

    <component
      :is="CurrentAttributeComponent"
      :attribute="attribute"
      :is-editing-view="isEditingView"
      @update="handleUpdate"
      @delete="handleDelete"
    />
    <TeamNumberRemoveModal
      v-if="showRemoveModal"
      :contact-name="selectedContact?.name || 'Contato'"
      :has-acelerity="hasAcelerity"
      :is-saving="removeBusy"
      :error="removeError"
      @close="showRemoveModal = false"
      @confirm="confirmRemove"
    />
  </div>
</template>
